/**
 * การรับเงิน คืนเงิน และเงินทอน
 *
 * บั๊กเดิมที่โครงนี้ปิด
 *   · makePayment ไม่เคยเทียบยอดชำระกับยอดบิล → 6 บิลยอดไม่ตรง
 *   · แยกจ่าย: เงินทอนถูกหักจาก "ทุก" บรรทัดเงินสด ถ้ามี 2 บรรทัดคือหัก 2 รอบ
 *   · ช่องทางที่ไม่ใช่เงินสด/โอน ได้รหัสเป็นค่าว่าง แล้วหลุดจากรายงาน
 *   · CN ถูก insert เป็นยอดบวกในตารางเดียวกับใบเสร็จ ทำให้ยอดเกินแทนที่จะหัก
 */

import { assertSatang, sumSatang } from './money.ts';
import type { PaymentRow, Tender } from './types.ts';

/** ยอดสุทธิที่รับมาของบิล — คืนเงินเป็นค่าลบอยู่แล้ว จึงบวกตรง ๆ ได้ */
export function paymentsNet(rows: readonly PaymentRow[]): number {
  return sumSatang(
    rows.map((r) => r.amountSatang),
    'payment.amountSatang',
  );
}

export interface CloseCheck {
  readonly ok: boolean;
  /** ขาดอีกเท่าไรถึงจะปิดบิลได้ · ติดลบแปลว่ารับเกิน */
  readonly shortfallSatang: number;
}

/**
 * ปิดบิลได้ก็ต่อเมื่อยอดที่บันทึกไว้เท่ากับยอดบิลเป๊ะ
 * ตรวจที่ server ในทรานแซกชันเดียวกับการปิดบิล ไม่ใช่ที่หน้าจอ
 */
export function canCloseBill(
  totalSatang: number,
  rows: readonly PaymentRow[],
): CloseCheck {
  assertSatang(totalSatang, 'totalSatang');
  const paid = paymentsNet(rows);
  return { ok: paid === totalSatang, shortfallSatang: totalSatang - paid };
}

export interface PaymentPlan {
  readonly rows: PaymentRow[];
  /** เงินทอน — คำนวณครั้งเดียว ไม่ว่าจะมีบรรทัดเงินสดกี่บรรทัด */
  readonly changeSatang: number;
}

/**
 * แปลง "เงินที่ลูกค้ายื่นให้" เป็น "บรรทัดที่จะบันทึกลง ledger"
 *
 * หลักการ: บันทึกสิ่งที่ร้าน *เก็บไว้* ไม่ใช่สิ่งที่ลูกค้า *ยื่นมา*
 *   · ช่องทางที่ไม่ใช่เงินสด บันทึกตามยอดที่เรียกเก็บจริง
 *   · เงินสดบันทึกเป็นส่วนที่เหลือ = ยอดบิล − ยอดช่องทางอื่น
 *   · เงินทอน = เงินสดที่ยื่นมา − เงินสดที่เก็บไว้   ← คำนวณครั้งเดียว
 *
 * ผลรวมของ rows จึงเท่ากับยอดบิลเสมอโดยโครงสร้าง
 */
export function buildPaymentPlan(
  totalSatang: number,
  tenders: readonly Tender[],
): PaymentPlan {
  assertSatang(totalSatang, 'totalSatang');
  if (tenders.length === 0) {
    throw new Error('ต้องมีการชำระเงินอย่างน้อย 1 รายการ');
  }
  for (const t of tenders) {
    assertSatang(t.amountSatang, 'tender.amountSatang');
    if (t.amountSatang <= 0) {
      throw new Error('ยอดชำระต้องมากกว่า 0');
    }
  }

  const cashTenders = tenders.filter((t) => t.methodKind === 'cash');
  const otherTenders = tenders.filter((t) => t.methodKind !== 'cash');

  const otherTotal = sumSatang(
    otherTenders.map((t) => t.amountSatang),
    'tender.amountSatang',
  );

  if (otherTotal > totalSatang) {
    throw new Error(
      `ยอดชำระที่ไม่ใช่เงินสด (${otherTotal}) มากกว่ายอดบิล (${totalSatang})`,
    );
  }

  const cashKept = totalSatang - otherTotal;

  if (cashKept > 0 && cashTenders.length === 0) {
    throw new Error(`ยังขาดอีก ${cashKept} สตางค์ ต้องรับเป็นเงินสด`);
  }

  const cashTendered = sumSatang(
    cashTenders.map((t) => t.amountSatang),
    'tender.amountSatang',
  );

  if (cashTendered < cashKept) {
    throw new Error(
      `เงินสดที่รับมา (${cashTendered}) น้อยกว่าที่ต้องเก็บ (${cashKept})`,
    );
  }

  const changeSatang = cashTendered - cashKept;

  const rows: PaymentRow[] = otherTenders.map((t) => ({
    methodId: t.methodId,
    methodKind: t.methodKind,
    kind: 'payment' as const,
    amountSatang: t.amountSatang,
  }));

  // เงินสดยุบเป็นบรรทัดเดียวเสมอ — ตรงกับสิ่งที่เกิดขึ้นจริงในลิ้นชัก
  // และทำให้เงินทอนถูกหักครั้งเดียวโดยอัตโนมัติ
  if (cashKept > 0) {
    rows.push({
      methodId: cashTenders[0]!.methodId,
      methodKind: 'cash',
      kind: 'payment',
      amountSatang: cashKept,
      tenderedSatang: cashTendered,
      changeSatang,
    });
  }

  return { rows, changeSatang };
}

/**
 * บรรทัดคืนเงิน — ยอดติดลบเสมอ อยู่ใน ledger เดียวกับการรับเงิน
 * ทำให้ SUM() ของรายงานถูกต้องโดยไม่ต้องมีเงื่อนไขพิเศษ
 */
export function buildRefundRow(
  methodId: number,
  methodKind: PaymentRow['methodKind'],
  amountSatang: number,
): PaymentRow {
  assertSatang(amountSatang, 'amountSatang');
  if (amountSatang <= 0) {
    throw new Error('ยอดคืนเงินต้องเป็นค่าบวก ระบบจะกลับเครื่องหมายให้เอง');
  }
  return {
    methodId,
    methodKind,
    kind: 'refund',
    amountSatang: -amountSatang,
  };
}

export interface RefundInput {
  /** คืนทั้งใบ หรือคืนบางรายการ */
  readonly scope: 'full' | 'partial';
  /** ผลรวมราคาของรายการที่คืน ก่อนเฉลี่ยส่วนลด — ใช้เฉพาะตอนคืนบางรายการ */
  readonly grossSatang: number;
  readonly subtotalSatang: number;
  readonly totalSatang: number;
  readonly alreadyRefundedSatang: number;
}

/**
 * ยอดที่จะคืนให้ลูกค้า
 *
 * ★ ต้องเฉลี่ยส่วนลดลงไปด้วย ★
 * ถ้าบิลมีส่วนลด 5% แล้วคืนของชิ้นเดียวโดยคืนราคาเต็ม เท่ากับคืนเกินกว่าที่
 * ลูกค้าจ่ายมาจริง — ร้านเสียเงินทุกครั้งที่มีการคืนของในบิลที่มีส่วนลด
 *
 * คืนทั้งใบใช้ยอดคงเหลือตรง ๆ ไม่ผ่านการเฉลี่ย จึงไม่มีเศษตกค้าง
 * และคืนเกินยอดที่เหลือไม่ได้เด็ดขาด
 */
export function computeRefundAmount(input: RefundInput): number {
  assertSatang(input.subtotalSatang, 'subtotalSatang');
  assertSatang(input.totalSatang, 'totalSatang');
  assertSatang(input.alreadyRefundedSatang, 'alreadyRefundedSatang');

  const remaining = input.totalSatang - input.alreadyRefundedSatang;
  if (remaining <= 0) {
    throw new Error('บิลนี้คืนเงินครบแล้ว คืนเพิ่มไม่ได้');
  }

  if (input.scope === 'full') return remaining;

  assertSatang(input.grossSatang, 'grossSatang');
  if (input.grossSatang <= 0) {
    throw new Error('ต้องเลือกรายการที่จะคืนอย่างน้อย 1 รายการ');
  }
  if (input.subtotalSatang <= 0) {
    throw new Error('บิลนี้ไม่มียอดสินค้า คืนเงินไม่ได้');
  }

  // เฉลี่ยส่วนลดตามสัดส่วนของรายการที่คืน แล้วปัดเป็นบาทเต็มแบบครึ่งขึ้น
  const share = (input.grossSatang * input.totalSatang) / input.subtotalSatang;
  const rounded = Math.round(share / 100) * 100;

  // คืนเกินยอดคงเหลือไม่ได้ — กันทั้งความผิดพลาดจากการปัดเศษและจากการกดซ้ำ
  return Math.min(rounded, remaining);
}

/** ยอดขายสุทธิของบิลใบเดียว */
export function netSaleOfBill(
  totalSatang: number,
  refundedSatang: number,
): number {
  assertSatang(totalSatang, 'totalSatang');
  assertSatang(refundedSatang, 'refundedSatang');
  return totalSatang - refundedSatang;
}
